import React, { useState, useEffect, useRef } from 'react';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { mockData } from '../mock';

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-advance testimonials
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + mockData.testimonials.length) % mockData.testimonials.length);
  };

  return (
    <section ref={sectionRef} className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-l from-blue-500/5 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }} />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <Quote className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Depoimentos</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              O que nossos clientes dizem
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            A confiança dos nossos clientes é nossa maior conquista. 
            Veja o que eles têm a dizer sobre nossos serviços.
          </p>
        </div>

        {/* Main Testimonial Display */}
        <div className={`relative max-w-4xl mx-auto ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
          <div className="glass-effect border border-cyan-400/30 rounded-lg p-8 lg:p-12 relative">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                <Quote className="w-6 h-6 text-black" />
              </div>
            </div>

            {/* Testimonial Content */}
            <div className="text-center">
              {/* Stars */}
              <div className="flex justify-center space-x-1 mb-6">
                {[...Array(mockData.testimonials[currentTestimonial].rating)].map((_, index) => (
                  <Star key={index} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Testimonial Text */}
              <blockquote className="body-large text-gray-300 mb-8 leading-relaxed italic">
                "{mockData.testimonials[currentTestimonial].text}"
              </blockquote>

              {/* Client Info */}
              <div className="flex items-center justify-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-full flex items-center justify-center border border-cyan-400/30">
                  <span className="text-cyan-400 font-bold text-xl">
                    {mockData.testimonials[currentTestimonial].name.charAt(0)}
                  </span>
                </div>
                
                <div className="text-left">
                  <h4 className="heading-3 text-white">
                    {mockData.testimonials[currentTestimonial].name}
                  </h4>
                  <p className="body-small text-cyan-400">
                    {mockData.testimonials[currentTestimonial].position}
                  </p>
                  <p className="body-small text-gray-400">
                    {mockData.testimonials[currentTestimonial].company}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center hover:border-cyan-400 hover:scale-110 transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5 text-cyan-400" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center hover:border-cyan-400 hover:scale-110 transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5 text-cyan-400" />
            </button>
          </div>
        </div>

        {/* Testimonial Indicators */}
        <div className={`flex justify-center space-x-3 mt-8 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
          {mockData.testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentTestimonial 
                  ? 'bg-cyan-400 scale-125' 
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
            />
          ))}
        </div>

        {/* All Testimonials Grid (Mobile View) */}
        <div className={`grid lg:hidden gap-6 mt-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.9s' }}>
          {mockData.testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`glass-effect border border-white/10 rounded-lg p-6 transition-all duration-300 ${
                index === currentTestimonial ? 'border-cyan-400/50 bg-cyan-400/5' : 'hover:border-cyan-400/30'
              }`}
            >
              {/* Stars */}
              <div className="flex space-x-1 mb-4">
                {[...Array(testimonial.rating)].map((_, starIndex) => (
                  <Star key={starIndex} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Text */}
              <p className="body-small text-gray-300 mb-4 italic">
                "{testimonial.text}"
              </p>

              {/* Client */}
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-full flex items-center justify-center border border-cyan-400/30">
                  <span className="text-cyan-400 font-bold text-sm">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                
                <div>
                  <h4 className="body-medium text-white font-semibold">{testimonial.name}</h4>
                  <p className="text-xs text-gray-400">{testimonial.position}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className={`text-center mt-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '1.2s' }}>
          <div className="glass-effect border border-cyan-400/30 rounded-lg p-8 max-w-3xl mx-auto">
            <h3 className="heading-3 text-white mb-4">
              Mais de <span className="text-cyan-400">200 empresas</span> confiam em nós
            </h3>
            
            <p className="body-medium text-gray-300 mb-6">
              Desde startups até grandes corporações, ajudamos organizações a 
              proteger seus ativos digitais com testes de segurança profissionais.
            </p>

            <div className="flex justify-center items-center space-x-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-400">98%</div>
                <div className="text-sm text-gray-400">Satisfação</div>
              </div>
              <div className="w-px h-10 bg-gray-600" />
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-400">500+</div>
                <div className="text-sm text-gray-400">Pentests</div>
              </div>
              <div className="w-px h-10 bg-gray-600" />
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-400">10+</div>
                <div className="text-sm text-gray-400">Anos</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;